<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="openCreateDialog"
      @updateSearch="(v) => (search = v)"
      headerText=""
      @fileUploaded="initialise"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="programs"
            :custom-filter="customFilter"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.action`]="{ item }">
              <v-icon
                :disabled="!item.active"
                small
                class="mr-2"
                color="accent"
                @click="edit(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.duration`]="{ item }">
              {{ getDurationType(item) }}
            </template>
            <template #[`item.degree_specialization`]="{ item }">
              {{ getProgramName(item) }}
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip
                small
                :color="item.active ? 'green' : 'red'"
                dark
                @click="openConfirm(item)"
              >
                {{ showActiveStatus(item) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="confirmCreate"
        @update="confirmUpdate"
        :degrees="degrees"
        :departments="departments"
        :program="program"
        :edit="editMode"
      />
    </v-dialog>
    <ConfirmDialog
      v-if="showDialog"
      :show="showDialog"
      @delete="changeConfirmation"
      @close="showDialog = false"
      title="program"
      :name="currentItem.degree.short_name + ' - ' + currentItem.specialization"
      :active="currentItem.active"
    />
    <!-- Create/Update Confirmation Dialog -->
    <v-dialog v-model="showActionConfirm" max-width="500" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Confirm {{ actionType }}
        </v-card-title>
        <v-card-text>
         
          <!-- Qualifications List -->
          <div v-if="selectedQualifications.length > 0" class="mt-4">
            <div class="font-weight-bold">Required Qualifications:</div>
            <v-chip-group column>
              <v-chip
                v-for="qual in selectedQualifications"
                :key="qual.id"
                small
                color="primary"
                outlined
              >
                {{ qual.name }}
              </v-chip>
            </v-chip-group>
          </div>
           <p class="mt-2">Are you sure you want to {{ actionType.toLowerCase() }} this program?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="cancelAction">Cancel</v-btn>
          <v-btn color="primary" :loading="loading" text @click="executeAction">Proceed</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/program/form.vue";
import searchBar from "@/components/searchBar.vue";
import { programHeaders } from "../constants/headers";
import { durations } from "../constants/enum";
import ConfirmDialog from "@/components/confirm.vue";

export default {
  components: {
    formDialog,
    searchBar,
    ConfirmDialog,
  },
  data() {
    return {
      dialog: false,
      editMode: false,
      program: {},
      programs: [],
      search: "",
      headers: programHeaders,
      degrees: [],
      departments: [],
      qualifications: [], // Add qualifications array
      durations: durations,
      showDialog: false,
      currentItem: {},
      // New data properties for confirmation
      showActionConfirm: false,
      actionType: "", // "Create" or "Update"
      actionData: null, // Holds the data to be created/updated
      pendingAction: null, // Stores the resolve function for promise
      selectedQualifications: [], // Stores qualification objects for display
      loading: false
    };
  },
  watch: {},
  computed: {},
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Programmes" }]);
    this.initialise();
    this.fetchQualifications(); // Fetch qualifications on load
  },
  methods: {
    async initialise() {
      this.getPrograms();
      this.fetchDegrees(); // Fetch degrees if needed
      this.fetchDepartments(); // Fetch departments if needed
    },
    
    async getPrograms() {
      await this.$axios
        .get("programs")
        .then((res) => {
          const response = res.data.data;
          this.programs = response.sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },

    // Fetch qualifications from API
    async fetchQualifications() {
      await this.$axios
        .get("qualifications")
        .then((res) => {
          this.qualifications = res.data.data || [];
        })
        .catch(async (error) => {
          console.error("Error fetching qualifications:", error);
          this.qualifications = [];
        });
    },

    // Fetch degrees if needed for degree name lookup
    async fetchDegrees() {
      await this.$axios
        .get("degrees")
        .then((res) => {
          this.degrees = res.data.data || [];
        })
        .catch((error) => {
          console.error("Error fetching degrees:", error);
        });
    },

    // Fetch departments if needed
    async fetchDepartments() {
      await this.$axios
        .get("departments")
        .then((res) => {
          this.departments = res.data.data || [];
        })
        .catch((error) => {
          console.error("Error fetching departments:", error);
        });
    },

    // Get degree name by ID
    getDegreeName(degreeId) {
      const degree = this.degrees.find(d => d.id === degreeId);
      return degree ? degree.short_name || degree.name : `Degree ID: ${degreeId}`;
    },

    // Get duration type name by ID
    getDurationTypeName(durationTypeId) {
      const durationObj = this.durations.find(d => d.id === durationTypeId);
      return durationObj ? durationObj.name : "";
    },

    // Get qualification objects from IDs
    getQualificationsFromIds(qualificationIds) {
      if (!qualificationIds || !Array.isArray(qualificationIds)) return [];
      return this.qualifications.filter(q => qualificationIds.includes(q.id));
    },

    // Open create dialog
    openCreateDialog() {
      this.dialog = true;
      this.editMode = false;
      this.program = {};
    },

    // Confirm before create
    confirmCreate(data) {
      this.actionData = data;
      this.actionType = "Create";
      // Fetch qualification objects for display
      this.selectedQualifications = this.getQualificationsFromIds(data.qualifications || []);
      this.showActionConfirm = true;
    },

    // Confirm before update
    confirmUpdate(data) {
      this.actionData = data;
      this.actionType = "Update";
      // Fetch qualification objects for display
      this.selectedQualifications = this.getQualificationsFromIds(data.qualifications || []);
      console.log(this.selectedQualifications);
      this.showActionConfirm = true;
    },

    async executeAction() {
      this.loading = true;
      if (this.actionType === "Create") {
        await this.createProgram(this.actionData);
        this.loading = false;
      } else if (this.actionType === "Update") {
        await this.updateProgram(this.actionData);
        this.loading = false;
      }
      this.cancelAction();
    },

    cancelAction() {
      this.showActionConfirm = false;
      this.actionData = null;
      this.actionType = "";
      this.selectedQualifications = [];
    },

    async createProgram(data) {
      await this.$axios
        .post("programs", { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    async updateProgram(data) {
      await this.$axios
        .put("programs/" + data.id, { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    
    changeConfirmation() {
      this.updateProgram({
        id: this.currentItem.id,
        active: !this.currentItem.active,
      });
    },
    
    openConfirm(item) {
      this.showDialog = true;
      this.currentItem = { ...item };
    },
    
    showActiveStatus(item) {
      return item.active ? "Active" : "Inactive";
    },

    edit(item) {
      this.program = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.showDialog = false;
      this.showActionConfirm = false;
      this.program = {};
      this.currentItem = {};
      this.actionData = null;
      this.actionType = "";
      this.selectedQualifications = [];
    },
    
    getDuration(item) {
      const durationObj = this.durations.find(
        (p) => p.id == item.duration_type
      );
      if (!durationObj) return "";
      const name = durationObj.name;
      return item.duration > 1 ? `${name}s` : name;
    },
    
    getDurationType(item) {
      return item.duration + " " + this.getDuration(item);
    },
    
    getProgramName(item) {
      return (
        (item.degree?.short_name || "") +
        (item.specialization ? ` - ${item.specialization}` : "")
      );
    },
    
    customFilter(value, search, item) {
      if (!search) return true;
      const degree = this.getProgramName(item);
      const duration = this.getDurationType(item);
      const activeStatus = this.showActiveStatus(item.active);
      return (
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase()) ||
        degree.toLowerCase().includes(search.toLowerCase()) ||
        duration.toLowerCase().includes(search.toLowerCase()) ||
        activeStatus.toLowerCase().includes(search.toLowerCase())
      );
    },
  },
};
</script>

<style></style>